// frontend/src/components/CallDetails.js

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { io } from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';
import { 
  User, Calendar, Clock, AlertCircle, MessageSquare,
   List, Tag, Send, ArrowLeft
} from 'lucide-react';
import { Container } from './ui/Container';
import { GradientButton } from './ui/GradientButton';
import api from '../api';

const SOCKET_IO_URL = process.env.REACT_APP_SOCKET_IO_URL || 'http://localhost:5000';

export default function CallDetails() {
  const { callId } = useParams(); // Get callId from route parameters
  const [call, setCall] = useState(null);
  const [response, setResponse] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [socket, setSocket] = useState(null);

  useEffect(() => {
    if (!callId) {
      setError('No Call ID provided.');
      return;
    }

    // Initialize Socket.io connection
    const newSocket = io(SOCKET_IO_URL); // Replace with your backend URL if different
    setSocket(newSocket);

    // Clean up on unmount
    return () => newSocket.close();
  }, [callId]);

  useEffect(() => {
    if (socket) {
      // Listen for transcript updates
      socket.on('transcript-update', (data) => {
        if (data.callId === callId) {
          setCall((prevCall) => ({
            ...prevCall,
            transcript: data.transcript,
          }));
          // Re-fetch summaries and other extractions based on updated transcript
          updateSummaries(data.transcript);
          updateName(data.transcript);
          updatePhoneNumber(data.transcript);
        }
      });

     /** REFACTOR all this shit is not used */

      // Listen for call updates (e.g., callerName, phoneNumber)
      socket.on('call-update', (data) => {
        if (data._id === callId) { // Changed from data.callId to data._id
          setCall((prevCall) => ({
            ...prevCall,
            callerName: data.callerName || prevCall.callerName,
            phoneNumber: data.phoneNumber || prevCall.phoneNumber,
            summary: data.summary || prevCall.summary,
            actionItems: data.actionItems || prevCall.actionItems,
            keywords: data.keywords || prevCall.keywords,
          }));
        }
      });

      // Listen for 'log-call-success' and 'log-call-error' events
      socket.on('log-call-success', (data) => {
        console.log('Log Call Success:', data);
        setCall(data.call);
      });

      socket.on('log-call-error', (data) => {
        console.error('Log Call Error:', data);
        setError(data.message);
      });

      // Clean up event listeners on unmount
      return () => {
        socket.off('transcript-update');
        socket.off('call-update');
        socket.off('log-call-success');
        socket.off('log-call-error');
      };
    }
  }, [socket, callId]);

  useEffect(() => {
    // Fetch call details on component mount
    const fetchCallDetails = async () => {
      try {
        const res = await api.get(`${SOCKET_IO_URL}/api/call-logs/${callId}`);
        const foundCall = res.data;
        setCall(foundCall);

        // Only call process-transcript if summary, actionItems, or keywords are missing
        if (!foundCall.summary || !foundCall.actionItems || !foundCall.keywords) {
          setLoading(true);
          try {
            // Fetch summary, action items, and keywords in parallel if needed
            const [summaryRes, actionItemsRes, keywordsRes] = await Promise.all([
              !foundCall.summary ? getSummary(foundCall.transcript) : Promise.resolve(foundCall.summary),
              !foundCall.actionItems ? getActionItems(foundCall.transcript) : Promise.resolve(foundCall.actionItems),
              !foundCall.keywords ? getKeywords(foundCall.transcript) : Promise.resolve(foundCall.keywords),
            ]);

            // Update call data with any new information
            setCall((prevCall) => ({
              ...prevCall,
              summary: summaryRes,
              actionItems: actionItemsRes,
              keywords: keywordsRes,
            }));
          } catch (err) {
            console.error('Call details Error processing transcript:', err);
            setError('Failed to process the transcript. Please try again later.');
          } finally {
            setLoading(false);
          }
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          console.log("Emitting log-call for a new call due to 404");
          if (socket) {
            socket.emit('log-call', {
              conversationId: uuidv4(), // Generate a unique conversationId
              callerName: "Unknown",  // Set default or adjust to match incoming data
              phoneNumber: "Unknown", // Set default or adjust to match incoming data
              // Add other fields as necessary
            });
          }
        } else {
          console.error('Error fetching call details:', error);
          setError('Failed to fetch call details. Please try again later.');
        }
      }
    };

    fetchCallDetails();
  }, [callId, socket]);

  const handleResponseSubmit = (e) => {
    e.preventDefault();
    // Send response via SMS or email
    // This is a placeholder. Implement actual sending logic.
    setMessage('Response sent successfully.');
    setTimeout(() => setMessage(''), 3000);
  };

  // Function to get summary
  const getSummary = async (transcript) => {
    const response = await callBackendAPI(transcript, 'summary');
    return response;
  };

  // Function to get action items
  const getActionItems = async (transcript) => {
    const response = await callBackendAPI(transcript, 'actionItems');
    return response;
  };

  // Function to get keywords
  const getKeywords = async (transcript) => {
    const response = await callBackendAPI(transcript, 'keywords');
    return response;
  };

  // Function to get caller's name
  const getName = async (transcript) => {
    const response = await callBackendAPI(transcript, 'extractName');
    return response;
  };

  // Function to get caller's phone number
  const getPhoneNumber = async (transcript) => {
    const response = await callBackendAPI(transcript, 'extractPhoneNumber');
    return response;
  };

  // Function to call the backend API
  const callBackendAPI = async (transcript, task) => {
    try {
      const res = await api.post(`${SOCKET_IO_URL}/api/process-transcript`, {
        callId, // Include _id to identify which call to update
        transcript,
        task,
      });
      return res.data.text;
    } catch (err) {
      console.error('Error calling backend API:', err);
      throw err;
    }
  };

  /**
   * Updates summaries, action items, keywords, name, and phone number based on the updated transcript.
   * @param {string} updatedTranscript - The latest transcript text.
   */
  const updateSummaries = async (updatedTranscript) => {
    setLoading(true);
    try {
      const [summaryRes, actionItemsRes, keywordsRes] = await Promise.all([
        getSummary(updatedTranscript),
        getActionItems(updatedTranscript),
        getKeywords(updatedTranscript),
      ]);

      setCall((prevCall) => ({
        ...prevCall,
        summary: summaryRes,
        actionItems: actionItemsRes,
        keywords: keywordsRes,
      }));
    } catch (err) {
      console.error('Error updating summaries:', err);
      setError('Failed to update the summaries based on the updated transcript.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Updates caller's name based on the updated transcript.
   * @param {string} updatedTranscript - The latest transcript text.
   */
  const updateName = async (updatedTranscript) => {
    try {
      const nameRes = await getName(updatedTranscript);
      setCall((prevCall) => ({
        ...prevCall,
        callerName: nameRes || prevCall.callerName,
      }));
    } catch (err) {
      console.error('Error updating caller name:', err);
      // Optionally set an error message or leave it
    }
  };

  /**
   * Updates caller's phone number based on the updated transcript.
   * @param {string} updatedTranscript - The latest transcript text.
   */
  const updatePhoneNumber = async (updatedTranscript) => {
    try {
      const phoneRes = await getPhoneNumber(updatedTranscript);
      setCall((prevCall) => ({
        ...prevCall,
        phoneNumber: phoneRes || prevCall.phoneNumber,
      }));
    } catch (err) {
      console.error('Error updating phone number:', err);
      // Optionally set an error message or leave it
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Container className="py-6 px-4 sm:px-6 lg:px-20">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="mt-1 text-sm text-red-600">{error}</p>
              </div>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (!call || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
          <span className="text-sm text-gray-600">Processing...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200">
        <Container className="py-6 px-4 sm:px-6 lg:px-20">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-4 lg:mb-0">
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => window.history.back()}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                    Call Details
                  </h1>
                  <p className="mt-2 text-sm text-gray-600">
                    View detailed information about this call
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Caller Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm"
            >
              <div className="flex items-center">
                <div className="p-2 bg-violet-50 rounded-lg">
                  <User className="w-6 h-6 text-violet-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Caller Name</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {call.callerName || 'Unknown'}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm"
            >
              <div className="flex items-center">
                <div className="p-2 bg-violet-50 rounded-lg">
                  <Calendar className="w-6 h-6 text-violet-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Date</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {new Date(call.timestamp).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm"
            >
              <div className="flex items-center">
                <div className="p-2 bg-violet-50 rounded-lg">
                  <Clock className="w-6 h-6 text-violet-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Time</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {new Date(call.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </Container>
      </div>

      {/* Content Section */}
      <Container className="py-6 px-4 sm:px-6 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">        

            {/* Transcript Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <MessageSquare className="w-5 h-5 text-violet-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Messages</h2>
                </div>
                <div className="space-y-2">
                  {call.messages && call.messages.length > 0 ? (
                    call.messages.map((msg) => (
                      <div key={msg._id} className="flex">
                        <span className="font-semibold mr-2">
                          {msg.role === 'user' ? 'User:' : 'Assistant:'}
                        </span>
                        <span className="text-gray-700">{msg.text}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-600">No messages available.</p>
                  )}
                </div>
              </div>
            </motion.div>


            {/* Summary Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <MessageSquare className="w-5 h-5 text-violet-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Summary</h2>
                </div>
                <p className="text-gray-600">
                  {call.summary || 'Summary not available.'}
                </p>
              </div>
            </motion.div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Action Items Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <List className="w-5 h-5 text-violet-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Action Items</h2>
                </div>
                <p className="text-gray-600">
                  {call.actionItems || 'No action items identified.'}
                </p>
              </div>
            </motion.div>

            {/* Keywords Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Tag className="w-5 h-5 text-violet-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Keywords</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  {call.keywords ? (
                    call.keywords.map((keyword, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full 
                          text-sm font-medium bg-violet-50 text-violet-700"
                      >
                        {keyword}
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-600">No keywords identified.</p>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Response Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Send className="w-5 h-5 text-violet-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Respond to Caller</h2>
                </div>
                <form onSubmit={handleResponseSubmit}>
                  <textarea
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                      focus:ring-violet-500 focus:border-violet-500"
                    rows="4"
                    placeholder="Type your response..."
                  />
                  <div className="mt-4">
                    <GradientButton type="submit">
                      Send Response
                    </GradientButton>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </Container>
    </div>
  );
}